import React from 'react'
import { useApp } from '../context/AppContext'
import { HEX, onColor } from '../lib/color'

/**
 * The mark shown wherever the product identifies itself — sidebar, mobile
 * header, sign-in screen, public certificate pages.
 *
 * It resolves in this order:
 *
 *   1. the addressed institution's own logo (`branding.logo_url`)
 *   2. the Gladhy mark below
 *
 * There is deliberately no institution-specific bitmap fallback. Shipping one
 * institution's artwork as the platform default is the bug this replaced:
 * OSCT's logo greeted every institution on Gladhy, including ones with nothing
 * to do with maritime work.
 */

/** Gladhy brand palette. Also in public/gladhy-mark.png (source of truth). */
export const BRAND = {
  amber: '#F2B85C',
  orange: '#E8963A',
  terracotta: '#C4552F',
  ink: '#2A1D17',
}

/**
 * The Gladhy mark: three ascending bars and a dot — a programme progressing
 * to certification.
 *
 * Drawn as SVG rather than loading public/gladhy-mark.png so it stays sharp at
 * every size and costs no network request. Geometry is measured from that PNG
 * (480px canvas, scaled to this 48-unit viewBox), so the two stay identical.
 *
 * The colours are fixed on purpose. An earlier version tinted the mark with
 * the institution's own colour, which is wrong twice over: a brand mark is not
 * a themeable shape, and this mark stands for Gladhy — not for whoever happens
 * to be signed in.
 */
export function GladhyMark({ size = 28, title = 'Gladhy' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      role="img"
      aria-label={title}
      style={{ flexShrink: 0, display: 'block' }}
    >
      <rect x="6" y="8.3" width="14.9" height="8.1" rx="4.05" fill={BRAND.amber} />
      <circle cx="37.5" cy="12.35" r="4.44" fill={BRAND.terracotta} />
      <rect x="6" y="19.9" width="25.4" height="8.1" rx="4.05" fill={BRAND.orange} />
      <rect x="6" y="31.5" width="35.9" height="8.1" rx="4.05" fill={BRAND.terracotta} />
    </svg>
  )
}

/**
 * "Powered by Gladhy" — the platform's own credit, for pages an institution's
 * visitors reach from outside: claiming a certificate, verifying one, browsing
 * a public registry. It says whose software this is without competing with the
 * institution, whose name and logo stay at the top of the page.
 *
 * Built from the mark plus the word rather than public/gladhy-logo.png: that
 * file has a solid white background baked in, so it would sit as a white
 * rectangle on the grey footer, and it carries wide empty margins that make it
 * render small for its box. A lockup assembled here is transparent, sharp at
 * any size, and adapts to whatever it is placed on.
 */
export function PoweredByGladhy({ color, markSize = 15 }) {
  const { branding } = useApp()
  // White-label (Enterprise): lembaga yang membayar untuk itu tidak memajang
  // kredit platform.
  if (branding?.hide_platform_branding) return null
  const ink = color || BRAND.ink
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
        lineHeight: 1,
      }}
    >
      <span style={{ fontSize: 10, letterSpacing: '0.04em', opacity: 0.65 }}>Powered by</span>
      <GladhyMark size={markSize} />
      <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '-0.01em', color: ink }}>
        Gladhy
      </span>
    </span>
  )
}

/**
 * The addressed institution's name, for the many small labels that used to say
 * "OSCT" outright — breadcrumbs, eyebrows, page headers.
 */
export function useOrgName() {
  const { branding } = useApp()
  return branding?.institution_name || 'Gladhy'
}

/**
 * Pengganti logo untuk lembaga white-label yang belum mengunggah logo: huruf
 * pertama namanya di atas warna lembaga. Tanpa ini lembaga itu menampilkan
 * tanda Gladhy — persis yang ingin disembunyikannya.
 */
function Monogram({ size, name, color, rounded }) {
  const bg = HEX.test(color || '') ? color : '#475569'
  return (
    <span
      role="img"
      aria-label={name}
      style={{
        width: size, height: size, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: rounded ? '50%' : 8,
        background: bg, color: onColor(bg),
        fontWeight: 600, fontSize: Math.round(size * 0.46), lineHeight: 1,
        userSelect: 'none',
      }}
    >
      {(name || '?').trim().charAt(0).toUpperCase()}
    </span>
  )
}

/**
 * @param {number}  size
 * @param {boolean} rounded  circular crop, for photographic institution logos
 */
export default function BrandLogo({ size = 28, rounded = true, branding: override }) {
  const ctx = useApp().branding
  // `branding` eksplisit dipakai pratinjau di Pengaturan, yang menampilkan
  // isian belum tersimpan; yang lain membaca lembaga yang sedang dialamati.
  const branding = override || ctx
  const src = branding?.logo_url

  if (!src) {
    return branding?.hide_platform_branding
      ? <Monogram size={size} name={branding.institution_name} color={branding.primary_color} rounded={rounded} />
      : <GladhyMark size={size} />
  }

  return (
    <img
      src={src}
      alt={branding?.institution_name || 'Logo'}
      width={size}
      height={size}
      style={{
        borderRadius: rounded ? '50%' : 8,
        objectFit: 'cover',
        flexShrink: 0,
        display: 'block',
      }}
    />
  )
}
